<script setup lang="ts">
const { t: $t } = useI18n()

interface UpdateData {
  id: string
  transportationCompanyName?: string | null
  transportationCompanyDriverName?: string | null
  transportationCompanyLicensePlateNumber?: string | null
}

const { openUpdateTransportationCompanyInfoPopup: { data: popupData, close: closePopup } } = useGlobalOpeners()

const payload = computed<UpdateData | undefined>(() => {
  if (!popupData.value?.payload.id) {
    return undefined
  }

  return popupData.value.payload
})

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const updateMutation = useMutation({
  mutationFn: $trpc.logisticsTask.updateTransportationCompanyInfo.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('transportationCompanyName.notification.update.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['logisticsTask'] })
    closePopup()
    notification.success({ title: $t('transportationCompanyName.notification.update.success'), duration: 4500 })
  },
})

function update() {
  if (!payload.value) {
    return
  }

  const { transportationCompanyDriverName, transportationCompanyLicensePlateNumber } = payload.value
  if (popupData.value?.allowUpdatingExtraFields) {
    payload.value.transportationCompanyDriverName = transportationCompanyDriverName || null
    payload.value.transportationCompanyLicensePlateNumber = transportationCompanyLicensePlateNumber || null
  }

  updateMutation.mutate(payload.value)
}
</script>

<template>
  <ThePopup
    v-if="payload"
    :title="$t('logisticsTask.transportationCompanyName.update.title')"
    show
    @close="closePopup"
  >
    <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
    <FormKit
      v-model="payload"
      type="form"
      @submit="update"
    >
      <FormKit
        name="transportationCompanyName"
        :label="$t('logisticsTask.transportationCompanyName.label')"
        type="text"
        validation="required"
      />
      <FormKit
        v-if="popupData?.allowUpdatingExtraFields"
        name="transportationCompanyDriverName"
        :label="$t('logisticsTask.reportedTransportationCompanyDriverName.label')"
        type="text"
      />
      <FormKit
        v-if="popupData?.allowUpdatingExtraFields"
        name="transportationCompanyLicensePlateNumber"
        :label="$t('logisticsTask.reportedTransportationCompanyLicensePlateNumber.label')"
        type="text"
      />
    </FormKit>
  </ThePopup>
</template>
